/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { ModelUsage, ModelUsage$inboundSchema } from "./modelusage.js";
import { ToolUsage, ToolUsage$inboundSchema } from "./toolusage.js";

/**
 * Aggregated metrics
 */
export type ProjectSummary = {
  /**
   * Average chat request duration in milliseconds
   */
  avgChatDurationMs: number;
  /**
   * Average chat resolution score (0-100)
   */
  avgChatResolutionScore: number;
  /**
   * Average tokens per chat request
   */
  avgTokensPerRequest: number;
  /**
   * Average tool call duration in milliseconds
   */
  avgToolDurationMs: number;
  /**
   * Sum of cache creation input tokens
   */
  cacheCreationInputTokens: number;
  /**
   * Sum of cache read input tokens
   */
  cacheReadInputTokens: number;
  /**
   * Chats abandoned by user
   */
  chatResolutionAbandoned: number;
  /**
   * Chats that failed to resolve
   */
  chatResolutionFailure: number;
  /**
   * Chats partially resolved
   */
  chatResolutionPartial: number;
  /**
   * Chats resolved successfully
   */
  chatResolutionSuccess: number;
  /**
   * Number of distinct models used (project scope only)
   */
  distinctModels: number;
  /**
   * Number of distinct providers used (project scope only)
   */
  distinctProviders: number;
  /**
   * Requests that completed naturally
   */
  finishReasonStop: number;
  /**
   * Requests that resulted in tool calls
   */
  finishReasonToolCalls: number;
  /**
   * Earliest activity timestamp in Unix nanoseconds
   */
  firstSeenUnixNano: string;
  /**
   * Latest activity timestamp in Unix nanoseconds
   */
  lastSeenUnixNano: string;
  /**
   * List of models used with call counts
   */
  models: Array<ModelUsage>;
  /**
   * Failed tool calls (4xx/5xx status)
   */
  toolCallFailure: number;
  /**
   * Successful tool calls (2xx status)
   */
  toolCallSuccess: number;
  /**
   * List of tools used with success/failure counts
   */
  tools: Array<ToolUsage>;
  /**
   * Total number of chat requests
   */
  totalChatRequests: number;
  /**
   * Number of unique chat sessions (project scope only)
   */
  totalChats: number;
  /**
   * Total cost of all requests
   */
  totalCost: number;
  /**
   * Sum of input tokens used
   */
  totalInputTokens: number;
  /**
   * Sum of output tokens used
   */
  totalOutputTokens: number;
  /**
   * Sum of all tokens used
   */
  totalTokens: number;
  /**
   * Total number of tool calls
   */
  totalToolCalls: number;
};

/** @internal */
export const ProjectSummary$inboundSchema: z.ZodMiniType<
  ProjectSummary,
  unknown
> = z.pipe(
  z.object({
    avg_chat_duration_ms: z.number(),
    avg_chat_resolution_score: z.number(),
    avg_tokens_per_request: z.number(),
    avg_tool_duration_ms: z.number(),
    cache_creation_input_tokens: z.int(),
    cache_read_input_tokens: z.int(),
    chat_resolution_abandoned: z.int(),
    chat_resolution_failure: z.int(),
    chat_resolution_partial: z.int(),
    chat_resolution_success: z.int(),
    distinct_models: z.int(),
    distinct_providers: z.int(),
    finish_reason_stop: z.int(),
    finish_reason_tool_calls: z.int(),
    first_seen_unix_nano: z.string(),
    last_seen_unix_nano: z.string(),
    models: z.array(ModelUsage$inboundSchema),
    tool_call_failure: z.int(),
    tool_call_success: z.int(),
    tools: z.array(ToolUsage$inboundSchema),
    total_chat_requests: z.int(),
    total_chats: z.int(),
    total_cost: z.number(),
    total_input_tokens: z.int(),
    total_output_tokens: z.int(),
    total_tokens: z.int(),
    total_tool_calls: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "avg_chat_duration_ms": "avgChatDurationMs",
      "avg_chat_resolution_score": "avgChatResolutionScore",
      "avg_tokens_per_request": "avgTokensPerRequest",
      "avg_tool_duration_ms": "avgToolDurationMs",
      "cache_creation_input_tokens": "cacheCreationInputTokens",
      "cache_read_input_tokens": "cacheReadInputTokens",
      "chat_resolution_abandoned": "chatResolutionAbandoned",
      "chat_resolution_failure": "chatResolutionFailure",
      "chat_resolution_partial": "chatResolutionPartial",
      "chat_resolution_success": "chatResolutionSuccess",
      "distinct_models": "distinctModels",
      "distinct_providers": "distinctProviders",
      "finish_reason_stop": "finishReasonStop",
      "finish_reason_tool_calls": "finishReasonToolCalls",
      "first_seen_unix_nano": "firstSeenUnixNano",
      "last_seen_unix_nano": "lastSeenUnixNano",
      "tool_call_failure": "toolCallFailure",
      "tool_call_success": "toolCallSuccess",
      "total_chat_requests": "totalChatRequests",
      "total_chats": "totalChats",
      "total_cost": "totalCost",
      "total_input_tokens": "totalInputTokens",
      "total_output_tokens": "totalOutputTokens",
      "total_tokens": "totalTokens",
      "total_tool_calls": "totalToolCalls",
    });
  }),
);

export function projectSummaryFromJSON(
  jsonString: string,
): SafeParseResult<ProjectSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ProjectSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ProjectSummary' from JSON`,
  );
}
